import { HelpTip } from '../HelpTip';
import { formatBytes } from '../../lib/formatBytes';

/** PDF, PNG or JPEG at 10 MB — the `user-documents` allowlist and cap the endpoints enforce. */
export const USER_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export const USER_DOCUMENT_ACCEPT = 'application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg';

/**
 * Files chosen for a `user-documents` upload — the shared file box. One file by default;
 * `multiple` lets the reader pick several at once, and the value is a list either way.
 *
 * **Laid out like every shared input**: one label line with the description in a `<HelpTip>` and
 * an `sr-only` twin on `aria-describedby`, the error below. Building Management's
 * `<MaintenanceDocumentInput>` is this same box with its label fixed; a form that needs another
 * label takes this one rather than a third copy.
 *
 * **The `accept` filter and the size check are conveniences, not gates** — the server verifies
 * each file's own leading bytes and enforces the cap. Each file over the cap is NAMED, because
 * with several chosen "that file is too large" does not say which.
 */
export function FileField({
  id,
  label,
  description,
  files,
  onChange,
  multiple = false,
  required = false,
  disabled = false,
  error,
}: {
  id: string;
  label: string;
  description: string;
  /** What is chosen now. Empty when nothing is. */
  files: File[];
  onChange: (files: File[]) => void;
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}) {
  const tooLarge = files.filter((file) => file.size > USER_DOCUMENT_MAX_BYTES);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-h-5 items-center gap-1.5">
        <label htmlFor={id} className="text-sm text-slate-700">
          {label}
          {required ? (
            <span className="ml-0.5 text-red-600" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>

        <HelpTip label={description} />
      </div>

      <span id={`${id}-desc`} className="sr-only">
        {description}
      </span>

      <input
        id={id}
        name={id}
        type="file"
        accept={USER_DOCUMENT_ACCEPT}
        multiple={multiple}
        required={required}
        disabled={disabled}
        aria-describedby={`${id}-desc`}
        aria-invalid={tooLarge.length > 0 || Boolean(error)}
        onChange={(event) => onChange(Array.from(event.target.files ?? []))}
        className="block w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50"
      />

      {tooLarge.length > 0 ? (
        tooLarge.map((file) => (
          <p key={`${file.name}-${file.size}`} className="text-xs text-red-700">
            {file.name} is {formatBytes(file.size)}, over the {formatBytes(USER_DOCUMENT_MAX_BYTES)} limit.
          </p>
        ))
      ) : error ? (
        <p className="text-xs text-red-700">{error}</p>
      ) : null}
    </div>
  );
}
